import { Alert, Flex, Loader } from "@mantine/core"
import { IconAlertCircle } from "@tabler/icons-react"
import { UseQueryResult } from "@tanstack/react-query"
import _ from "lodash"
import { usePostHog } from "posthog-js/react"
import { JSX } from "react"
import { Navigate } from "react-router-dom"

type AlertProps = {
  title?: string
  message: string
}

type DontAllowNullProps<TData, TError> = {
  query: UseQueryResult<TData, TError>
  children: (props: { data: TData }) => JSX.Element | null
  errorAlert?: AlertProps
  emptyAlert?: AlertProps
  allowEmptyArray?: boolean
  allowNull?: false
  LoadingComponent?: JSX.Element
  defaultData?: unknown
}

type AllowNullProps<TData, TError> = {
  query: UseQueryResult<TData | null, TError>
  children: (props: { data: TData | null }) => JSX.Element | null
  errorAlert?: AlertProps
  emptyAlert?: AlertProps
  allowEmptyArray?: boolean
  allowNull: true
  LoadingComponent?: JSX.Element
  defaultData?: unknown
}

type Props<TData, TError> =
  | DontAllowNullProps<TData, TError>
  | AllowNullProps<TData, TError>

const ErrorRedirect = ({ error }: { error: unknown }) => {
  if (
    error &&
    typeof error === "object" &&
    // @ts-expect-error error is not typed
    error.error === "NO_ACCESS_REDIRECT_HOME"
  ) {
    return <Navigate to="/home" replace />
  }
  return null
}

export const QueryWrapper = <TData, TError>({
  query,
  allowEmptyArray = false,
  allowNull = false,
  errorAlert,
  emptyAlert,
  LoadingComponent = <Loader />,
  defaultData,
  children,
}: Props<TData, TError>) => {
  const posthog = usePostHog()
  const { data, isLoading, isError, error } = query

  if (defaultData && isLoading) {
    return children({ data: defaultData as TData })
  }

  if (isError) {
    posthog.capture("Explorer Error Encountered", {
      error: typeof error === "object" ? JSON.stringify(error) : String(error),
      location: window.location.pathname,
    })

    return (
      <>
        <ErrorRedirect error={error} />
        <Flex align="center" justify="center" p={32}>
          <Alert
            color="red"
            title={errorAlert?.title ?? "Something went wrong"}
            icon={<IconAlertCircle />}
          >
            {errorAlert?.message ??
              "We couldn't proceed your request. Please try again or contact our support."}
          </Alert>
        </Flex>
      </>
    )
  }

  if (isLoading)
    return (
      <Flex align="center" justify="center" p={32} h="100%">
        {LoadingComponent}
      </Flex>
    )

  if (data === null && allowNull) {
    return (children as AllowNullProps<TData, TError>["children"])({
      data: null,
    })
  }

  if (!data || (_.isEmpty(data) && !allowEmptyArray)) {
    return (
      <Flex align="center" justify="center" p={32}>
        <Alert
          color="red"
          title={emptyAlert?.title ?? "No results"}
          icon={<IconAlertCircle />}
        >
          {emptyAlert?.message ??
            "We couldn‘t find any results for your request."}
        </Alert>
      </Flex>
    )
  }

  return children({ data })
}
